import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
    SpinnerGap,
    Calendar,
    Clock,
    Tag,
    SignOut,
    Eye,
    BookOpen,
    PaperPlaneTilt,
    DownloadSimple,
    ShareNetwork,
    WhatsappLogo,
    Sparkle,
    MapPin,
    X,
    ShieldCheck,
    Phone,
    Globe
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import RichContent from '../components/RichContent';
import { fetchCustomerMe, clearCustomerToken, getCustomerToken, transferCertificate } from '../services/customerApi';

const fmt = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(n || 0);
const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' }); }
    catch { return iso.slice(0, 10); }
};

const CustomerPortalPage = () => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [viewCert, setViewCert] = useState(null);
    const [bookCert, setBookCert] = useState(null);
    const [transferCert, setTransferCert] = useState(null);
    const [transferForm, setTransferForm] = useState({ new_name: '', new_email: '', new_phone: '' });
    const [transferLoading, setTransferLoading] = useState(false);

    useEffect(() => {
        if (!getCustomerToken()) {
            navigate('/me/login', { replace: true });
            return;
        }
        (async () => {
            try {
                const res = await fetchCustomerMe();
                setData(res.data);
            } catch (err) {
                if (err.response?.status === 401) {
                    clearCustomerToken();
                    navigate('/me/login', { replace: true });
                } else {
                    // Muestra el detalle del backend si está disponible para diagnosticar
                    const detail = err.response?.data?.detail || err.message || 'error desconocido';
                    toast.error(`Error al cargar tus certificados: ${detail}`);
                }
            } finally {
                setLoading(false);
            }
        })();
    }, [navigate]);

    const logout = () => {
        clearCustomerToken();
        navigate('/me/login', { replace: true });
    };

    const shareWhatsApp = (cert) => {
        const text = `¡Mira mi certificado de viaje! ${cert.product_name} · Folio ${cert.folio} · ${cert.cert_url}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    };

    const handleTransferSubmit = async (e) => {
        e.preventDefault();
        if (!transferForm.new_name.trim() || !transferForm.new_email.trim()) {
            toast.error('Completa nombre y correo del nuevo titular');
            return;
        }
        if (!window.confirm(`¿Confirmas transferir "${transferCert.product_name}" a ${transferForm.new_email}? Esta acción no se puede deshacer.`)) {
            return;
        }
        setTransferLoading(true);
        try {
            await transferCertificate({
                sale_id: transferCert.sale_id,
                ...transferForm
            });
            toast.success('Certificado transferido con éxito');
            setTransferCert(null);
            setTransferForm({ new_name: '', new_email: '', new_phone: '' });
            const res = await fetchCustomerMe();
            setData(res.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'No se pudo transferir');
        } finally {
            setTransferLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F4ECE0] flex items-center justify-center">
                <SpinnerGap size={32} className="animate-spin text-[#1F2D4B]" />
            </div>
        );
    }
    if (!data) return null;

    const activeCerts = data.certificates.filter(c => !c.expired);
    const expiredCerts = data.certificates.filter(c => c.expired);

    return (
        <div className="min-h-screen bg-[#F4ECE0]" data-testid="customer-portal">
            {/* Premium hero header with brand logo */}
            <header className="relative bg-[#1F2D4B] overflow-hidden" data-testid="portal-hero">
                {/* Subtle radial accent */}
                <div
                    aria-hidden="true"
                    className="absolute inset-0 opacity-[0.18] pointer-events-none"
                    style={{
                        background:
                            'radial-gradient(ellipse 60% 80% at 50% 0%, rgba(251,113,133,0.45), transparent 60%)'
                    }}
                />
                {/* Top utility row (customer name + logout) */}
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-5 flex items-center justify-between text-white/80">
                    <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-white/60">
                        {data.company.name}
                    </p>
                    <div className="flex items-center gap-2">
                        <span className="hidden sm:inline text-xs text-white/70 mr-1">
                            {data.customer.name}
                        </span>
                        <button
                            onClick={logout}
                            data-testid="customer-logout-btn"
                            className="h-9 w-9 sm:w-auto sm:px-3 rounded-md border border-white/15 hover:bg-white/10 flex items-center justify-center gap-1.5 text-xs text-white/90 transition-colors"
                        >
                            <SignOut size={14} weight="bold" />
                            <span className="hidden sm:inline">Salir</span>
                        </button>
                    </div>
                </div>

                {/* Centered large brand logo */}
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-8 sm:pt-10 pb-3 flex justify-center">
                    <img
                        src="/brand/exploremos-logo-white.png"
                        alt="Exploremos"
                        className="h-20 sm:h-28 md:h-32 w-auto select-none drop-shadow-[0_4px_24px_rgba(251,113,133,0.25)]"
                        data-testid="portal-brand-logo"
                        draggable="false"
                    />
                </div>

                {/* Tagline */}
                <p className="relative text-center text-[10px] sm:text-[11px] uppercase tracking-[0.32em] text-white/55 pb-10 sm:pb-14 font-medium">
                    Viajar · para volver a ti
                </p>

                {/* Coral accent line */}
                <div
                    aria-hidden="true"
                    className="absolute bottom-0 left-0 right-0 h-[3px]"
                    style={{
                        background:
                            'linear-gradient(90deg, #1F2D4B 0%, #FB7185 50%, #1F2D4B 100%)'
                    }}
                />
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-8">
                {/* Greeting */}
                <div>
                    <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1F2D4B]">
                        ¡Hola, {data.customer.name.split(' ')[0]}!
                    </h2>
                    <p className="text-sm text-[#4B5878] mt-1">
                        Tienes <b>{activeCerts.length}</b> certificado(s) activo(s)
                        {expiredCerts.length > 0 && ` y ${expiredCerts.length} vencido(s)`}.
                    </p>
                </div>

                {/* Active certificates grid */}
                {activeCerts.length === 0 ? (
                    <div className="bg-white border border-[#ECE3D2] rounded-2xl p-8 text-center">
                        <p className="text-[#4B5878]">Aún no tienes certificados activos.</p>
                    </div>
                ) : (
                    <section data-testid="active-certificates">
                        <h3 className="font-semibold text-[#1F2D4B] mb-4 text-sm uppercase tracking-wider">Activos</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                            {activeCerts.map(cert => (
                                <CertCard
                                    key={cert.item_id || cert.folio}
                                    cert={cert}
                                    onView={() => setViewCert(cert)}
                                    onBook={() => setBookCert(cert)}
                                    onTransfer={() => setTransferCert(cert)}
                                    onShare={() => shareWhatsApp(cert)}
                                />
                            ))}
                        </div>
                    </section>
                )}

                {/* Expired */}
                {expiredCerts.length > 0 && (
                    <section data-testid="expired-certificates">
                        <h3 className="font-semibold text-[#8B8474] mb-3 text-sm uppercase tracking-wider">Vencidos</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                            {expiredCerts.map(cert => (
                                <CertCard key={cert.item_id || cert.folio} cert={cert} expired
                                    onView={() => setViewCert(cert)}
                                    onBook={() => {}} onTransfer={() => {}} onShare={() => shareWhatsApp(cert)} />
                            ))}
                        </div>
                    </section>
                )}

                {/* Upsell */}
                {data.upsell && data.upsell.length > 0 && (
                    <section className="border-t border-[#ECE3D2] pt-8" data-testid="upsell-section">
                        <div className="flex items-center gap-2 mb-2">
                            <Sparkle size={18} weight="fill" className="text-[#FB7185]" />
                            <h3 className="font-display text-xl font-bold text-[#1F2D4B]">Te puede interesar</h3>
                        </div>
                        <p className="text-sm text-[#4B5878] mb-5">Otras experiencias para tu próximo viaje</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {data.upsell.slice(0, 6).map(p => (
                                <div key={p.id} className="bg-white border border-[#ECE3D2] rounded-xl overflow-hidden hover:shadow-md transition-shadow" data-testid={`upsell-${p.id}`}>
                                    {p.design?.background_image && (
                                        <div className="h-32 bg-cover bg-center" style={{ backgroundImage: `url(${p.design.background_image})` }} />
                                    )}
                                    <div className="p-4">
                                        <p className="font-display text-base font-bold text-[#1F2D4B] line-clamp-1">{p.name}</p>
                                        <p className="text-xs text-[#8B8474] line-clamp-1 mt-0.5">{p.destination}</p>
                                        <div className="flex items-center justify-between mt-3">
                                            <span className="font-mono text-base font-bold text-[#FB7185]">{fmt(p.price)}</span>
                                            <a
                                                href={data.company.phone ? `tel:${data.company.phone}` : (data.company.website || '#')}
                                                target={data.company.phone ? undefined : '_blank'}
                                                rel={data.company.phone ? undefined : 'noopener noreferrer'}
                                                className="text-xs px-3 h-8 rounded-md bg-[#1F2D4B] hover:bg-[#2E3F60] text-white font-medium flex items-center"
                                            >
                                                Cotizar
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Security footer */}
                <div className="text-[11px] text-[#8B8474] text-center pt-4 border-t border-[#ECE3D2] flex items-center justify-center gap-1.5">
                    <ShieldCheck size={12} weight="duotone" className="text-[#2D7A52]" />
                    Sesión protegida · expira automáticamente en 30 días
                </div>
            </main>

            {/* View Modal */}
            <Dialog open={!!viewCert} onOpenChange={(o) => !o && setViewCert(null)}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-2xl rounded-2xl max-h-[92vh] overflow-y-auto overflow-x-hidden">
                    {viewCert && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl text-[#1F2D4B]">{viewCert.product_name}</DialogTitle>
                                <DialogDescription className="flex items-center gap-2 text-[#8B8474]">
                                    <Tag size={12} /> Folio <span className="font-mono font-semibold text-[#1F2D4B]">{viewCert.folio}</span>
                                </DialogDescription>
                            </DialogHeader>
                            {viewCert.background_image && (
                                <div className="h-48 -mx-6 -mt-2 mb-4 bg-cover bg-center" style={{ backgroundImage: `url(${viewCert.background_image})` }} />
                            )}
                            <div className="space-y-4">
                                <div className="flex flex-wrap gap-4 text-xs">
                                    <span className="flex items-center gap-1.5 text-[#4B5878]">
                                        <MapPin size={13} weight="duotone" /> {viewCert.destination || '—'}
                                    </span>
                                    <span className="flex items-center gap-1.5 text-[#4B5878]">
                                        <Calendar size={13} weight="duotone" /> Comprado: {fmtDate(viewCert.purchased_at)}
                                    </span>
                                    <span className="flex items-center gap-1.5 text-[#4B5878]">
                                        <Clock size={13} weight="duotone" /> Vence: {fmtDate(viewCert.expires_at)} ({viewCert.days_left} días)
                                    </span>
                                </div>
                                {viewCert.full_description && (
                                    <div>
                                        <p className="text-[10px] uppercase tracking-wider text-[#8B8474] font-semibold mb-1">Descripción</p>
                                        <RichContent value={viewCert.full_description} className="text-sm text-[#4B5878]" />
                                    </div>
                                )}
                                {viewCert.terms_and_conditions && (
                                    <div className="border-t border-[#ECE3D2] pt-3">
                                        <p className="text-[10px] uppercase tracking-wider text-[#8B8474] font-semibold mb-1">Términos y condiciones</p>
                                        <RichContent value={viewCert.terms_and_conditions} className="text-xs text-[#4B5878]" />
                                    </div>
                                )}
                                {viewCert.cert_url && (
                                    <a
                                        href={viewCert.cert_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="open-cert-btn"
                                        className="inline-flex items-center gap-1.5 text-sm text-[#1F2D4B] underline hover:text-[#FB7185]"
                                    >
                                        <DownloadSimple size={14} weight="bold" /> Abrir certificado público / PDF
                                    </a>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Book Modal */}
            <Dialog open={!!bookCert} onOpenChange={(o) => !o && setBookCert(null)}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-lg rounded-2xl max-h-[92vh] overflow-y-auto">
                    {bookCert && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display text-xl text-[#1F2D4B] flex items-center gap-2">
                                    <BookOpen size={20} weight="duotone" className="text-[#FB7185]" /> Cómo reservar
                                </DialogTitle>
                                <DialogDescription>{bookCert.product_name}</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                                {bookCert.how_to_book ? (
                                    <RichContent value={bookCert.how_to_book} className="text-sm text-[#4B5878]" />
                                ) : (
                                    <p className="text-sm text-[#4B5878]">Contáctanos para coordinar tu reservación:</p>
                                )}
                                <div className="bg-[#F8F4EC] rounded-md p-3 space-y-2 text-sm">
                                    {data.company.phone && (
                                        <a href={`tel:${data.company.phone}`} className="flex items-center gap-2 text-[#1F2D4B] hover:text-[#FB7185]">
                                            <Phone size={14} weight="duotone" /> <b>{data.company.phone}</b>
                                        </a>
                                    )}
                                    {data.company.email && (
                                        <a href={`mailto:${data.company.email}?subject=Reservación folio ${bookCert.folio}`} className="flex items-center gap-2 text-[#1F2D4B] hover:text-[#FB7185]">
                                            <PaperPlaneTilt size={14} weight="duotone" /> {data.company.email}
                                        </a>
                                    )}
                                    {data.company.website && (
                                        <a href={data.company.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#1F2D4B] hover:text-[#FB7185]">
                                            <Globe size={14} weight="duotone" /> {data.company.website}
                                        </a>
                                    )}
                                </div>
                                <p className="text-[11px] text-[#8B8474]">Cuando contactes, menciona tu folio <span className="font-mono font-bold text-[#1F2D4B]">{bookCert.folio}</span> para agilizar el proceso.</p>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Transfer Modal */}
            <Dialog open={!!transferCert} onOpenChange={(o) => !o && setTransferCert(null)}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-lg rounded-2xl">
                    {transferCert && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display text-xl text-[#1F2D4B] flex items-center gap-2">
                                    <PaperPlaneTilt size={20} weight="duotone" className="text-[#FB7185]" /> Transferir certificado
                                </DialogTitle>
                                <DialogDescription>{transferCert.product_name} · folio {transferCert.folio}</DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleTransferSubmit} className="space-y-3 mt-2">
                                <div className="bg-[#FFF8E7] border border-[#F5DEB3] rounded-md p-3 text-[11px] text-[#7A4F00]">
                                    ⚠️ Esta acción transfiere la propiedad del certificado. Una vez confirmada, sólo el nuevo titular podrá usarlo. <b>No se puede deshacer.</b>
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-[#4B5878]">Nombre completo del nuevo titular</label>
                                    <input
                                        type="text"
                                        value={transferForm.new_name}
                                        onChange={(e) => setTransferForm({ ...transferForm, new_name: e.target.value })}
                                        data-testid="transfer-name-input"
                                        required
                                        className="mt-1 w-full h-11 px-3 rounded-md border border-[#ECE3D2] focus:outline-none focus:ring-2 focus:ring-[#1F2D4B]/20 focus:border-[#1F2D4B]"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-[#4B5878]">Correo del nuevo titular</label>
                                    <input
                                        type="email"
                                        value={transferForm.new_email}
                                        onChange={(e) => setTransferForm({ ...transferForm, new_email: e.target.value })}
                                        data-testid="transfer-email-input"
                                        required
                                        className="mt-1 w-full h-11 px-3 rounded-md border border-[#ECE3D2] focus:outline-none focus:ring-2 focus:ring-[#1F2D4B]/20 focus:border-[#1F2D4B]"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-[#4B5878]">Teléfono (opcional)</label>
                                    <input
                                        type="tel"
                                        value={transferForm.new_phone}
                                        onChange={(e) => setTransferForm({ ...transferForm, new_phone: e.target.value })}
                                        data-testid="transfer-phone-input"
                                        className="mt-1 w-full h-11 px-3 rounded-md border border-[#ECE3D2] focus:outline-none focus:ring-2 focus:ring-[#1F2D4B]/20 focus:border-[#1F2D4B]"
                                    />
                                </div>
                                <div className="flex gap-2 pt-2">
                                    <Button type="button" variant="outline" onClick={() => setTransferCert(null)} className="flex-1 h-11 rounded-md border-[#ECE3D2]">
                                        Cancelar
                                    </Button>
                                    <Button type="submit" disabled={transferLoading} data-testid="confirm-transfer-btn" className="flex-1 h-11 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md">
                                        {transferLoading ? (<><SpinnerGap size={14} className="animate-spin mr-1.5" /> Transfiriendo…</>) : 'Confirmar transferencia'}
                                    </Button>
                                </div>
                            </form>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

const CertCard = ({ cert, expired, onView, onBook, onTransfer, onShare }) => (
    <div className={`bg-white border border-[#ECE3D2] rounded-2xl overflow-hidden ${expired ? 'opacity-60' : 'hover:shadow-md'} transition-shadow`} data-testid={`cert-card-${cert.folio}`}>
        <div
            className="h-40 bg-cover bg-center relative"
            style={{ backgroundImage: cert.background_image ? `url(${cert.background_image})` : 'linear-gradient(135deg, #1F2D4B, #4B5878)' }}
        >
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-3 left-3 right-3 text-white">
                <p className="font-display text-lg font-bold leading-tight drop-shadow line-clamp-2">{cert.product_name}</p>
                <p className="text-[11px] opacity-90 font-mono mt-1">{cert.folio}</p>
            </div>
            {expired && (
                <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wider bg-[#C8413A] text-white px-2 py-0.5 rounded-full font-semibold">Vencido</span>
            )}
            {!expired && cert.days_left < 30 && (
                <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wider bg-[#D97706] text-white px-2 py-0.5 rounded-full font-semibold">
                    Vence en {cert.days_left}d
                </span>
            )}
        </div>
        <div className="p-3.5 space-y-3">
            <div className="flex items-center justify-between text-[11px] text-[#4B5878]">
                <span className="flex items-center gap-1"><MapPin size={11} weight="duotone" /> {cert.destination || '—'}</span>
                <span className="flex items-center gap-1"><Clock size={11} weight="duotone" /> {expired ? 'Vencido' : `${cert.days_left} días`}</span>
            </div>

            <div className="grid grid-cols-2 gap-1.5">
                <button
                    type="button"
                    onClick={onView}
                    data-testid={`view-cert-${cert.folio}`}
                    className="h-9 text-xs rounded-md bg-[#1F2D4B] hover:bg-[#2E3F60] text-white font-medium flex items-center justify-center gap-1"
                >
                    <Eye size={13} weight="bold" /> Ver
                </button>
                {!expired ? (
                    <button
                        type="button"
                        onClick={onBook}
                        data-testid={`book-cert-${cert.folio}`}
                        className="h-9 text-xs rounded-md border border-[#1F2D4B] text-[#1F2D4B] hover:bg-[#F8F4EC] font-medium flex items-center justify-center gap-1"
                    >
                        <BookOpen size={13} weight="bold" /> Reservar
                    </button>
                ) : (
                    <button
                        type="button"
                        disabled
                        className="h-9 text-xs rounded-md border border-[#ECE3D2] text-[#8B8474] cursor-not-allowed flex items-center justify-center gap-1"
                    >
                        <X size={13} /> Vencido
                    </button>
                )}
            </div>
            <div className="grid grid-cols-2 gap-1.5">
                {!expired && (
                    <button
                        type="button"
                        onClick={onTransfer}
                        data-testid={`transfer-cert-${cert.folio}`}
                        className="h-8 text-[11px] rounded-md border border-[#ECE3D2] text-[#4B5878] hover:bg-[#F8F4EC] font-medium flex items-center justify-center gap-1"
                    >
                        <PaperPlaneTilt size={11} weight="bold" /> Transferir
                    </button>
                )}
                <button
                    type="button"
                    onClick={onShare}
                    className={`h-8 text-[11px] rounded-md border border-[#ECE3D2] text-[#4B5878] hover:bg-[#F8F4EC] font-medium flex items-center justify-center gap-1 ${expired ? 'col-span-2' : ''}`}
                >
                    <WhatsappLogo size={12} weight="bold" /> Compartir
                </button>
            </div>
        </div>
    </div>
);

export default CustomerPortalPage;
